import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { useCallback } from 'react';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { doCustomVizsEquate } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomVizDoViz';

/**
 * If request is null, cancel the currently running custom viz request
 */
export type OnUserAnalysisCancelCallback = (
  customVizRequestToCancel: CustomVizRequest | null,
) => void;

export interface UseOnUserAnalysisCancelCallbackParams {
  /**
   * This will only be called when there is a lastSuccessfulCustomVizRequest
   * present so the lastSuccessfulCustomVizRequest is required
   */
  onShareLinkLoaded: (lastSuccessfulCustomVizRequest: CustomVizRequest) => void;
  resetDimensionsToInitialState: Nullable<() => void>;
  resetMetricsToInitialState: Nullable<() => void>;
  resetFiltersToInitialState: Nullable<() => void>;
}

export interface UseUserAnalysisStackAndSpinnerPayload {
  onUserAnalysisCancel: OnUserAnalysisCancelCallback;
}

/**
 * The purpose of this hook is to make it 100% explicit on how to handle a user
 * analysis cancellation.
 *
 * Logic:
 * - If customVizRequestToCancel is null, cancel the currently running viz and
 *   skip the rest of the logic
 * - If customVizRequestToCancel is present, but doesn't match the currently
 *   running viz, skip all logic in this hook.
 *
 * - If customVizRequestToCancel is present and matches the currently running
 *   viz, then we'll:
 *   - Cancel the currently running viz
 *   - If we have a last successful request, we will fallback to that using the
 *     onShareLinkLoaded callback
 *   - If we don't have a last successful request, we will fallback to a default
 *     state by restoring all filters, metrics and dimensions to their initial
 *     state.
 * @see useDynamicFilters().resetFiltersToInitialState
 * @see useDynamicMetrics().resetMetricsToInitialState
 * @see useDynamicDimensions().resetDimensionsToInitialState
 */
export const useOnUserAnalysisCancelCallback = ({
  onShareLinkLoaded,
  resetFiltersToInitialState,
  resetMetricsToInitialState,
  resetDimensionsToInitialState,
}: UseOnUserAnalysisCancelCallbackParams) => {
  const {
    cancelViz,
    currentInProgressCustomVizRequest,
    lastSuccessfulCustomVizRequest,
  } = useCustomViz();

  return useCallback(
    (customVizRequestToCancel: CustomVizRequest | null) => {
      if (customVizRequestToCancel === null) {
        cancelViz();
        return;
      }

      const currentCustomVizRequestIsBeingCancelled = doCustomVizsEquate(
        currentInProgressCustomVizRequest,
        customVizRequestToCancel,
      );

      if (!currentCustomVizRequestIsBeingCancelled) {
        return;
      }

      cancelViz();

      if (lastSuccessfulCustomVizRequest) {
        onShareLinkLoaded(lastSuccessfulCustomVizRequest);
      } else {
        resetFiltersToInitialState?.();
        resetMetricsToInitialState?.();
        resetDimensionsToInitialState?.();
      }
    },
    [
      onShareLinkLoaded,
      resetDimensionsToInitialState,
      resetMetricsToInitialState,
      resetFiltersToInitialState,
      cancelViz,
      currentInProgressCustomVizRequest,
      lastSuccessfulCustomVizRequest,
    ],
  );
};
